// Copyright (c) Bentley Systems

export type ExtractedVariable = {
  name: string;
  dataType: string;        // from XML DataType attribute (e.g., "string", "int32")
  defaultValue?: string;   // from <Value><String>...</String></Value>
  defaultPreview?: string;   // preview of default value
};

export type VariableValidationRule = {
  variableName: string;
  validationTypeId: VariableValidationType;
  isRequired: boolean;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  allowDecimals?: boolean;
  regexPattern?: string;
  regexDescription?: string;
  dateFormat?: string;
  allowedValues?: string;  // comma-separated for Enum type
};

export enum VariableValidationType {
  None = "None",
  String = "String",
  Number = "Number",
  Guid = "Guid",
  Boolean = "Boolean",
  Date = "Date",
  DateTime = "DateTime",
  Enum = "Enum",
}

export const RESERVED_VARIABLE_NAMES = new Set(
  [
    "RunId", "ProjectId", "projectID", "ITwinId", "ITwinAccountId",
    "SubjectId", "MethodOfCaller", "OrganizationId", "organizationID",
    "UserOrganizationId", "TOKEN_PARAMETERS",
    "CurrentDate", "CurrentDateTime", "CurrentDateISO",
    "CurrentDateTimeISO", "CurrentDateTimeISO2",
    "$LastError", "$RowsRead", "SEQUENCEID", "Project.Name",
  ].map((name) => name.toLowerCase())
);

export const OUTPUT_VARIABLE_NAMES = new Set(
  [
    "PROJECTRESULT",
    "PROJECTRESULTSTR",
    "PROJECTRESULT_ERROR",
    "PROJECTRESULT_ERROR_MESSAGE",
  ].map((name) => name.toLowerCase())
);

export type ExtractVariablesResult = {
  configurable: ExtractedVariable[];
  reserved: ExtractedVariable[];
};

export function extractVariablesFromXml(xmlString: string): ExtractVariablesResult {
  const empty: ExtractVariablesResult = { configurable: [], reserved: [] };

  if (!xmlString?.trim()) {
    return empty;
  }

  //Parse XML string into a DOM Document
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, "application/xml");

  // Check for <parsererror> in case XML malformed
  if (doc.getElementsByTagName("parsererror").length > 0) {
    return empty;
  }

  // Find the <Variables> container
  const variablesContainer = doc.getElementsByTagName("Variables")[0];
  if (!variablesContainer) {
    return empty;
  }

  //get name, dataType, defaultValue for each <Variable>
  const configurable: ExtractedVariable[] = [];
  const reserved: ExtractedVariable[] = [];
  const seenNames = new Set<string>();

  const variableElements = variablesContainer.getElementsByTagName("Variable");
  for (const el of variableElements) {
    const name = el.getAttribute("Name");
    const dataType = el.getAttribute("DataType") ?? "";

    if (!name) continue;

    // Deduplicate — keep only the first occurrence of each name
    if (seenNames.has(name.toLowerCase())) continue;
    seenNames.add(name.toLowerCase());

    // Extract the optional default value
    let defaultValue: string | undefined;
    let defaultPreview: string | undefined;
    const valueEl = el.getElementsByTagName("Value")[0];
    if (valueEl) {
      const text = valueEl.textContent?.trim();
      if (text) {
        defaultValue = text;

        //get previewdefault value
        defaultPreview = defaultValue.split("/").at(-1);
      }
    }
    const variable: ExtractedVariable = { name, dataType, defaultValue, defaultPreview };

    if (OUTPUT_VARIABLE_NAMES.has(name.toLowerCase())) {
      continue;
    }

    // Separate reserved/system variables from configurable ones.
    if (RESERVED_VARIABLE_NAMES.has(name.toLowerCase())) {
      reserved.push(variable);
    } else {
      configurable.push(variable);
    }
  }

  // Sort configurable variables alphabetically
  configurable.sort((a, b) => a.name.localeCompare(b.name));

  return { configurable, reserved };
}
